import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountBalanceWallet as WalletIcon,
  Refresh as RefreshIcon,
  EventAvailable as EventAvailableIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
  WarningAmber as WarningIcon,
  Info as InfoIcon,
  TrendingUp as TrendingUpIcon,
  EventBusy as EventBusyIcon,
  FilterAltOff as FilterOffIcon,
  BeachAccess as BeachIcon,
  Sick as SickIcon,
  WbSunny as SunIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { fetchTeacherLeaveBalanceSelf } from "./api/teacherHrApi";
import { getStoredAcademicYearSessionId } from "./helpers";
import "./teacherHr.css";

/* -------------------- helpers -------------------- */

function leaveTypeMeta(type) {
  const k = String(type || "").toLowerCase();
  if (k.includes("casual"))
    return {
      hex: "#0ea5e9",
      bg: "#e0f2fe",
      icon: <SunIcon fontSize="small" />,
    };
  if (k.includes("sick"))
    return {
      hex: "#ef4444",
      bg: "#fee2e2",
      icon: <SickIcon fontSize="small" />,
    };
  if (k.includes("earned") || k.includes("paid"))
    return {
      hex: "#10b981",
      bg: "#d1fae5",
      icon: <BeachIcon fontSize="small" />,
    };
  if (
    k.includes("personal") ||
    k.includes("maternity") ||
    k.includes("paternity")
  )
    return {
      hex: "#8b5cf6",
      bg: "#ede9fe",
      icon: <PersonIcon fontSize="small" />,
    };
  return {
    hex: "#6366f1",
    bg: "#eef2ff",
    icon: <WalletIcon fontSize="small" />,
  };
}

function timeAgo(d) {
  if (!d) return "just now";
  const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}

/* -------------------- component -------------------- */

export default function LeaveBalances() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [lowOnly, setLowOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sessionId = getStoredAcademicYearSessionId();
      const res = await fetchTeacherLeaveBalanceSelf(
        sessionId ? { academicYearSession: sessionId } : {},
      );
      const data = res?.data?.data;
      setRows(Array.isArray(data) ? data : []);
      setLastRefresh(new Date());
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to load balances",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const enrichedRows = useMemo(() => {
    return rows.map((r) => {
      const allocated = Number(r.allocated || 0) + Number(r.carryForward || 0);
      const used = Number(r.used || 0);
      const pending = Number(r.pending || 0);
      const remaining = r.remaining ?? Math.max(0, allocated - used - pending);
      const pctUsed = allocated > 0 ? Math.round((used / allocated) * 100) : 0;
      const pctCommitted =
        allocated > 0 ? Math.round(((used + pending) / allocated) * 100) : 0;
      const isLow = remaining <= 2 && remaining > 0;
      const isExhausted = remaining <= 0;
      return {
        ...r,
        allocated,
        used,
        pending,
        remaining,
        pctUsed,
        pctCommitted,
        isLow,
        isExhausted,
        meta: leaveTypeMeta(r.leaveType),
      };
    });
  }, [rows]);

  const totals = useMemo(() => {
    if (!enrichedRows.length) return null;
    return enrichedRows.reduce(
      (acc, r) => ({
        allocated: acc.allocated + r.allocated,
        used: acc.used + r.used,
        pending: acc.pending + r.pending,
        remaining: acc.remaining + r.remaining,
      }),
      { allocated: 0, used: 0, pending: 0, remaining: 0 },
    );
  }, [enrichedRows]);

  const visibleRows = useMemo(() => {
    if (!lowOnly) return enrichedRows;
    return enrichedRows.filter((r) => r.isLow || r.isExhausted);
  }, [enrichedRows, lowOnly]);

  const lowCount = useMemo(
    () => enrichedRows.filter((r) => r.isLow || r.isExhausted).length,
    [enrichedRows],
  );

  const overallPct = totals
    ? Math.round((totals.used / Math.max(1, totals.allocated)) * 100)
    : 0;

  const resetFilters = () => setLowOnly(false);

  /* -------------------- render -------------------- */

  return (
    <Box
      className="orange-scrollBar"
      sx={{
        maxHeight: 600,
        overflow: "auto",
        p: { xs: 1.5, sm: 2 },
        bgcolor: "#f8fafc",
      }}
    >
      {/* -------------------- Header -------------------- */}
      <Box className="mb-3">
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                bgcolor: "#0ea5e9",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(14,165,233,0.25)",
                flexShrink: 0,
              }}
            >
              <WalletIcon />
            </Box>
            <Box>
              <Typography
                variant="h6"
                className="fw-bold"
                sx={{ lineHeight: 1.2 }}
              >
                Leave Balances
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overview of your allocated, used, and remaining leave days
              </Typography>
            </Box>
          </Stack>

          {/* Session + refresh meta */}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              size="small"
              icon={<InfoIcon sx={{ fontSize: 14 }} />}
              label="Current Session"
              sx={{
                bgcolor: "#eef2ff",
                color: "#4338ca",
                fontWeight: 700,
                fontSize: 11,
                height: 24,
                "& .MuiChip-icon": { color: "#4338ca" },
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Updated {timeAgo(lastRefresh)}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {/* -------------------- Summary Hero Cards -------------------- */}
      {totals && (
        <Grid container spacing={1.5} className="mb-3">
          {/* Total Allocated */}
          <Grid item xs={6} sm={4} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all .2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                  borderColor: "#6366f1",
                },
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  "&:last-child": { pb: 2 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    bgcolor: "#eef2ff",
                    color: "#6366f1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <WalletIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontSize: 11,
                    }}
                  >
                    Allocated
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "#6366f1",
                      lineHeight: 1.1,
                    }}
                  >
                    {totals.allocated}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Used */}
          <Grid item xs={6} sm={4} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all .2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                  borderColor: "#ef4444",
                },
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  "&:last-child": { pb: 2 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    bgcolor: "#fee2e2",
                    color: "#ef4444",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <AccessTimeIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontSize: 11,
                    }}
                  >
                    Used
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "#ef4444",
                      lineHeight: 1.1,
                    }}
                  >
                    {totals.used}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Pending */}
          <Grid item xs={6} sm={4} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all .2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                  borderColor: "#f59e0b",
                },
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  "&:last-child": { pb: 2 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    bgcolor: "#fef3c7",
                    color: "#f59e0b",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <EventAvailableIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontSize: 11,
                    }}
                  >
                    Pending
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "#f59e0b",
                      lineHeight: 1.1,
                    }}
                  >
                    {totals.pending}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Total Remaining with ring */}
          <Grid item xs={6} sm={4} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid #e5e7eb",
                height: "100%",
                transition: "all .2s ease",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.06)",
                  borderColor: "#10b981",
                },
              }}
            >
              <CardContent
                sx={{
                  p: 2,
                  "&:last-child": { pb: 2 },
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "50%",
                      background: `conic-gradient(#10b981 ${
                        overallPct * 3.6
                      }deg, #e5e7eb 0deg)`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#10b981",
                      }}
                    >
                      <CheckCircleIcon fontSize="small" />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: 0.4,
                      fontSize: 11,
                    }}
                  >
                    Remaining
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: "#10b981",
                      lineHeight: 1.1,
                    }}
                  >
                    {totals.remaining}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary", fontSize: 10.5 }}
                  >
                    {overallPct}% used
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* -------------------- Main Card -------------------- */}
      <Card
        elevation={0}
        sx={{
          borderRadius: 3,
          border: "1px solid #e5e7eb",
          overflow: "hidden",
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid #eef2f7",
            bgcolor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ flexWrap: "wrap", rowGap: 1 }}
            >
              <InfoIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  color: "text.secondary",
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Session Breakdown
              </Typography>
              <Chip
                size="small"
                label={`${enrichedRows.length} type${
                  enrichedRows.length !== 1 ? "s" : ""
                }`}
                sx={{
                  bgcolor: "#eef2ff",
                  color: "#4f46e5",
                  fontWeight: 700,
                  fontSize: 11,
                  height: 22,
                }}
              />
              {lowCount > 0 && (
                <Chip
                  size="small"
                  icon={<WarningIcon sx={{ fontSize: 14 }} />}
                  label={`${lowCount} low`}
                  onClick={() => setLowOnly((v) => !v)}
                  sx={{
                    bgcolor: lowOnly ? "#f59e0b" : "#fef3c7",
                    color: lowOnly ? "#fff" : "#92400e",
                    fontWeight: 700,
                    fontSize: 11,
                    height: 22,
                    cursor: "pointer",
                    "& .MuiChip-icon": {
                      color: lowOnly ? "#fff" : "#92400e",
                    },
                    "&:hover": {
                      bgcolor: lowOnly ? "#d97706" : "#fde68a",
                    },
                  }}
                />
              )}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              {lowOnly && (
                <Tooltip title="Clear filter">
                  <IconButton
                    size="small"
                    onClick={resetFilters}
                    sx={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 1.5,
                    }}
                  >
                    <FilterOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={loading ? null : <RefreshIcon fontSize="small" />}
                onClick={load}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: 1.5,
                  px: 2,
                  boxShadow: "none",
                  bgcolor: "#0ea5e9",
                  "&:hover": { bgcolor: "#0284c7" },
                }}
              >
                {loading ? "Loading…" : "Refresh"}
              </Button>
            </Stack>
          </Stack>
        </Box>

        {/* Table */}
        <TableContainer
          className="orange-scrollBar"
          sx={{ maxHeight: 460, overflow: "auto", width: "100%" }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{ width: "100%", minWidth: 780 }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: "#f8fafc",
                    color: "#475569",
                    fontWeight: 700,
                    fontSize: 12,
                    textTransform: "uppercase",
                    letterSpacing: 0.4,
                    borderBottom: "1px solid #e5e7eb",
                    py: 1.25,
                  },
                }}
              >
                <TableCell sx={{ minWidth: 180 }}>Leave Type</TableCell>
                <TableCell align="right" sx={{ minWidth: 110 }}>
                  Allocated
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  Used
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 100 }}>
                  Pending
                </TableCell>
                <TableCell align="right" sx={{ minWidth: 120 }}>
                  Remaining
                </TableCell>
                <TableCell sx={{ minWidth: 220 }}>Usage</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton width="70%" />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton width={50} sx={{ ml: "auto" }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton width={50} sx={{ ml: "auto" }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton width={50} sx={{ ml: "auto" }} />
                    </TableCell>
                    <TableCell align="right">
                      <Skeleton width={60} sx={{ ml: "auto" }} />
                    </TableCell>
                    <TableCell>
                      <Skeleton width="100%" />
                    </TableCell>
                  </TableRow>
                ))
              ) : visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 7 }}>
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        bgcolor: "#f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                      }}
                    >
                      {enrichedRows.length === 0 ? (
                        <WalletIcon
                          sx={{ fontSize: 34, color: "action.disabled" }}
                        />
                      ) : (
                        <EventBusyIcon
                          sx={{ fontSize: 34, color: "action.disabled" }}
                        />
                      )}
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      {enrichedRows.length === 0
                        ? "No balance rows found"
                        : "No low-balance leave types"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1.5 }}
                    >
                      {enrichedRows.length === 0
                        ? "Your school may assign leave policy from the admin app."
                        : "All your leave types have healthy balances."}
                    </Typography>
                    {enrichedRows.length > 0 && lowOnly && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={resetFilters}
                        sx={{ textTransform: "none", borderRadius: 1.5 }}
                      >
                        Clear filter
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((r, idx) => {
                  const barColor = r.isExhausted
                    ? "#ef4444"
                    : r.isLow
                      ? "#f59e0b"
                      : r.pctUsed >= 70
                        ? "#f59e0b"
                        : "#10b981";

                  return (
                    <TableRow
                      key={r._id || r.leaveType || idx}
                      hover
                      sx={{
                        bgcolor:
                          r.isExhausted || r.isLow
                            ? "#fffbeb"
                            : idx % 2 === 0
                              ? "#fff"
                              : "#fafbfc",
                        "&:hover": {
                          bgcolor:
                            r.isExhausted || r.isLow ? "#fef3c7" : "#f1f5f9",
                        },
                        "& td": {
                          borderBottom: "1px solid #eef2f7",
                          py: 1.25,
                          verticalAlign: "middle",
                        },
                      }}
                    >
                      <TableCell>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1.25}
                        >
                          <Box
                            sx={{
                              width: 34,
                              height: 34,
                              borderRadius: 1.75,
                              bgcolor: r.meta.bg,
                              color: r.meta.hex,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {r.meta.icon}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 700, fontSize: 13 }}
                            >
                              {r.leaveType}
                            </Typography>
                            {(r.isExhausted || r.isLow) && (
                              <Typography
                                sx={{
                                  fontSize: 10.5,
                                  fontWeight: 700,
                                  color: r.isExhausted ? "#b91c1c" : "#b45309",
                                  textTransform: "uppercase",
                                  letterSpacing: 0.3,
                                }}
                              >
                                {r.isExhausted ? "Exhausted" : "Running low"}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 700, fontSize: 13 }}
                        >
                          {r.allocated}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: "#ef4444",
                          }}
                        >
                          {r.used}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: "#f59e0b",
                          }}
                        >
                          {r.pending || "—"}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Chip
                          size="small"
                          label={r.remaining}
                          sx={{
                            bgcolor: r.isExhausted
                              ? "#fee2e2"
                              : r.isLow
                                ? "#fef3c7"
                                : "#d1fae5",
                            color: r.isExhausted
                              ? "#b91c1c"
                              : r.isLow
                                ? "#92400e"
                                : "#065f46",
                            fontWeight: 800,
                            fontSize: 12,
                            height: 24,
                            border: `1px solid ${
                              r.isExhausted
                                ? "#fca5a5"
                                : r.isLow
                                  ? "#fcd34d"
                                  : "#86efac"
                            }`,
                            minWidth: 36,
                          }}
                        />
                      </TableCell>

                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Tooltip
                            title={`${r.pctUsed}% used · ${Math.min(
                              r.pctCommitted,
                              100,
                            )}% committed (incl. pending)`}
                          >
                            <Box sx={{ flex: 1, minWidth: 100 }}>
                              <LinearProgress
                                variant="determinate"
                                value={Math.min(r.pctUsed, 100)}
                                sx={{
                                  height: 8,
                                  borderRadius: 4,
                                  bgcolor: "#f1f5f9",
                                  "& .MuiLinearProgress-bar": {
                                    bgcolor: barColor,
                                    borderRadius: 4,
                                  },
                                }}
                              />
                            </Box>
                          </Tooltip>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 800,
                              color: barColor,
                              minWidth: 36,
                              textAlign: "right",
                              fontSize: 11.5,
                            }}
                          >
                            {r.pctUsed}%
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer */}
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: "#f8fafc",
            borderTop: "1px solid #eef2f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <TrendingUpIcon fontSize="small" sx={{ color: "text.secondary" }} />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              Tracking{" "}
              <Box component="span" sx={{ fontWeight: 700, color: "#0f172a" }}>
                {enrichedRows.length} leave type
                {enrichedRows.length !== 1 ? "s" : ""}
              </Box>
              {totals && (
                <>
                  {" · "}
                  <Box
                    component="span"
                    sx={{ fontWeight: 700, color: "#10b981" }}
                  >
                    {totals.remaining} days
                  </Box>{" "}
                  remaining
                </>
              )}
            </Typography>
          </Stack>

          <Typography
            variant="caption"
            sx={{ color: "text.secondary", fontWeight: 500 }}
          >
            {loading ? "Loading…" : `Updated ${timeAgo(lastRefresh)}`}
          </Typography>
        </Box>
      </Card>
    </Box>
  );
}
